<template>
  <v-container>
    <v-alert
      v-model="isError"
      dismissible
      type="error"
      transition="fade-transition"
    >
      {{ errMessage }}
    </v-alert>
    <h1>Subscriptions</h1>
    <v-data-table
      :headers="headers"
      :items="subscriptions"
    >
      <template slot="items" slot-scope="props">
        <td>{{ props.item.scope }}</td>
        <td>{{ props.item.name }}</td>
        <td>{{ props.item.billingMethodName || props.item.billingMethodID }}</td>
        <td>{{ props.item.pricingDefinitionName || 'Loading...' }}</td>
        <td>{{ props.item.startDate.substr(0, 10) }}</td>
        <td>{{ props.item.endDate.substr(0, 10) }}</td>
        <td>{{ (props.item.cancelDate) ? props.item.cancelDate.substr(0, 10) : '' }}</td>
        <td>{{ props.item.hasHold || 'No' }}</td>
        <td>
          <v-icon
            small
            @click="editSubscription(props.item)"
          >edit
          </v-icon>
          <v-icon
            small
            @click="deleteSubscription(props.item)"
          >delete_forever
          </v-icon>
          <v-icon
            small
            @click="$router.push('/admin/subscriptions/' + props.item.id + '/invoices')"
          >receipt
          </v-icon>
          <v-icon
            small
            @click="$router.push('/admin/subscriptions/' + props.item.id + '/holds')"
          >report
          </v-icon>
        </td>
      </template>
    </v-data-table>
    <v-btn to="/admin/subscriptions/new">Add</v-btn>
  </v-container>
</template>

<script>
export default {
  data: () => ({
    isError: false,
    errMessage: null,
    subscriptions: [],
    headers: [
      { text: 'Type', value: 'scope' },
      { text: 'Name', value: 'name' },
      { text: 'Billing Method', value: 'billingMethodID' },
      { text: 'Pricing', value: 'pricingDefinitionID' },
      { text: 'Start', value: 'startDate' },
      { text: 'End', value: 'endDate' },
      { text: 'Canceled?', value: 'cancelDate' },
      { text: 'Has Hold?', value: 'hasHold' },
      { text: 'Actions', value: 'name' } // no actual value
    ]
  }),
  mounted: function () {
    var _this = this

    // Load information about subscriptions
    this.axios.get('/subscriptions')
      // Get basic subscription information
      .then(function (res) {
        // Populate some temporary name values before we send our next request
        // for real values
        for (var i in res.data) {
          res.data[i].name = 'Loading...'
        }
        _this.subscriptions = res.data
      })
      // Get a name to display for each subscription
      .then(function () {
        for (var i in _this.subscriptions) {
          var s = _this.subscriptions[i]
          var url = '/churches/' + s.scopeID
          if (s.scope === 'user') {
            url = '/auth/users/' + s.scopeID
          }
          _this.axios.get(url, {
            data: {
              scope: s.scope, // these variables aren't used by the request,
              scopeID: s.scopeID, // but we need to reference them later
              id: s.id
            }
          })
            .then(function (res) {
              var name = 'N/A'

              // Should never happen, but we check to be sure
              if (res.data.length > 0) {
                var r = res.data[0]
                var config = JSON.parse(res.config.data)

                switch (config.scope) {
                  case 'user':
                    // Get an email address to further specify the user beyond
                    // their name
                    var email
                    if (r.local.email) {
                      email = r.local.email
                    } else if (r.google.email) {
                      email = r.google.email
                    } else if (r.facebook.email) {
                      email = r.facebook.email
                    } else {
                      email = 'N/A'
                    }

                    name = r.firstName + ' ' + r.lastName + ' (' + email + ')'
                    break
                  case 'church':
                    name = r.name + ' (' + r.city + ', ' + r.state + ')'
                    break
                  default:
                    // should never happen
                    break
                }
              }

              // Find the subscription's index so we can modify it
              for (var i in _this.subscriptions) {
                if (_this.subscriptions[i].id === config.id) {
                  _this.subscriptions[i].name = name
                  break
                }
              }
              // All we want to do is update _this.subscriptions, but changing
              // properties in this context doesn't propagate a rendering update,
              // so we force the creation of a new object instead.
              // TODO: Find a better way to force the rendering update.
              _this.subscriptions = _this.subscriptions.slice()
            })
            .catch(_this.$root.apiErrorHandler())
        }
      })
      // Get a Yes/No value to show whether or not the subscription has a current hold
      .then(() => {
        for (let i in this.subscriptions) {
          let s = this.subscriptions[i]
          this.axios.get('/subscriptions/' + s.id + '/holds')
            .then((res) => {
              if (res.data && res.data.length > 0) {
                // Results are sorted most recent first, so we only need to check
                // the first element
                if (res.data[0].dateRemoved === null) {
                  // Find the subscription and update its hasHold value
                  for (let i in this.subscriptions) {
                    if (this.subscriptions[i].id === res.data[0].subscriptionID) {
                      this.subscriptions[i].hasHold = 'Yes'
                      // TODO: Find a better way to force the rendering update than re-slicing
                      this.subscriptions = this.subscriptions.slice()
                    }
                  }
                }
              }
            })
            .catch(_this.$root.apiErrorHandler())
        }
      })
      // Get names for billing methods
      .then(() => {
        this.axios.get('/subscriptions/billing/methods')
          .then((res) => {
            res.data.forEach((method) => {
              this.subscriptions.forEach((sub) => {
                if (sub.billingMethodID === method.id) {
                  sub.billingMethodName = method.title
                }
              })
            })

            // TODO: Find a better way to force the rendering update than re-slicing
            this.subscriptions = this.subscriptions.slice()
          })
          .catch(this.$root.apiErrorHandler((msg) => {
            this.errMessage = msg
            this.isError = true
          }))
      })
      // Get names for pricing definitions
      .then(() => {
        this.axios.get('/subscriptions/pricing/definitions')
          .then((res) => {
            // Get a list of definitions keyed by ID number
            let definitions = []
            for (let d of res.data) {
              definitions[d.id] = d
            }

            // Now get the pricing applied to each of our subscriptions
            let reqs = []
            for (let sub of this.subscriptions) {
              reqs.push(this.axios.get('/subscriptions/' + sub.id + '/pricing'))
            }
            this.axios.all(reqs)
              .then((results) => {
                for (let res of results) {
                  let pricing = res.data[0]
                  // Find the subscription associated with this pricing record
                  // and give it a pricing value
                  for (let sub of this.subscriptions) {
                    if (sub.id === pricing.subscriptionID) {
                      sub.pricingDefinitionName = definitions[pricing.definitionID].title
                      break
                    }
                  }
                }

                // TODO: Find a better way to force the rendering update than re-slicing
                this.subscriptions = this.subscriptions.slice()
              })
              .catch(this.$root.apiErrorHandler((msg) => {
                this.errMessage = msg
                this.isError = true
              }))
          })
          .catch(this.$root.apiErrorHandler((msg) => {
            this.errMessage = msg
            this.isError = true
          }))
      })
      .catch(this.$root.apiErrorHandler())
  },
  methods: {
    editSubscription: function (s) {
      this.$router.push('/admin/subscriptions/' + s.id)
    },
    deleteSubscription: function (s) {
      // Confirm with the user that they want to perform the requested action
      // before actually making the request
      var _this = this
      confirm('Do you want to cancel this subscription?') &&
      this.axios.delete('/subscriptions/' + s.id)
        .then(function () {
          // Find the index of our subscription in the list
          var index = null
          for (var i in _this.subscriptions) {
            if (_this.subscriptions[i].id === s.id) {
              index = i
              break
            }
          }
          if (index !== null) {
            // Update the cancelDate value for this subscription
            _this.subscriptions[index].cancelDate = _this.$root.getApiDateTimeString()
          } else {
            _this.errMessage = 'An error occurred. Please try again.'
            _this.isError = true
          }
        })
        .catch(this.$root.apiErrorHandler(function (msg) {
          _this.errMessage = msg
          _this.isError = true
        }))
    }
  }
}
</script>
